import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, ArrowLeft, Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const { loginAdmin } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.email || !form.password) {
      toast.error(lang === 'en' ? 'Fill all fields' : 'Isi semua kolom');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, form);
      loginAdmin(res.data.token, res.data.admin);
      toast.success(lang === 'en' ? 'Welcome!' : 'Selamat datang!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen t-bg flex items-center justify-center relative">
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="t-text-secondary hover:opacity-80 transition" data-testid="admin-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button onClick={toggleTheme} data-testid="admin-login-theme-toggle" className="p-2 rounded-full glass t-text-secondary">
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>

      <div className="w-full max-w-md mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#C7867A]/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#C7867A]" />
          </div>
          <h1 className="text-3xl font-bold t-text mb-2">{t('admin_login')}</h1>
          <p className="t-text-muted font-body text-sm">Match Group Indonesia</p>
        </motion.div>

        <div className="glass-card rounded-2xl p-8 space-y-4">
          <div>
            <label className="t-text-muted text-xs uppercase tracking-wider mb-1.5 block">{t('email')}</label>
            <Input data-testid="admin-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="t-bg-input t-border focus:border-[#C7867A] h-14 rounded-xl text-lg px-6 t-text" placeholder="admin@matchgroupid.com" />
          </div>
          <div>
            <label className="t-text-muted text-xs uppercase tracking-wider mb-1.5 block">{t('password')}</label>
            <Input data-testid="admin-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
              className="t-bg-input t-border focus:border-[#C7867A] h-14 rounded-xl text-lg px-6 t-text" placeholder="********"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
          </div>
        </div>

        <Button data-testid="admin-submit-btn" onClick={handleSubmit} disabled={loading}
          className="w-full mt-6 bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-full h-14 text-lg font-medium active:scale-95 disabled:opacity-50 shadow-[0_0_20px_rgba(199,134,122,0.4)]">
          {loading ? '...' : t('login')}
        </Button>
      </div>
    </div>
  );
}
